/**
 * KanbanDealBoard — Drag-and-drop deal pipeline board (Trello replacement)
 * KOPS-555: Reads from lead_ai_context.consumer_journey_stage, updates via PATCH
 *
 * Columns: Hot Lead → Application → Pre-Qualified → Under Contract →
 *          Disclosures → Initial Approval → Closing Docs → Funded
 *          + collapsed: Cold/Lost, Credit Repair
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faEnvelope,
  faStickyNote,
  faClock,
  faChevronDown,
  faChevronRight,
  faSpinner,
  faGripVertical,
  faCalendarCheck,
  faSearch,
  faSyncAlt,
  faFire,
  faFileAlt,
  faCheckCircle,
  faHandshake,
  faClipboardList,
  faThumbsUp,
  faFolder,
  faTrophy,
  faSnowflake,
  faHeart,
  faTimes,
  faMapMarkerAlt,
  faUser,
  faHistory,
  faArrowRight,
  faDollarSign,
  faHome,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { colors, fonts } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DealCard {
  id: string;
  borrowerName: string;
  phone: string;
  email: string;
  source: string;
  sourceTag: string;
  referringAgent: string;
  loanType: string;
  loanProgram: string;
  daysInStage: number;
  coeDate: string | null;
  lastNote: { text: string; date: string; type: string } | null;
  confidence: number | null;
  stageUpdatedAt: string;
}

// ---------------------------------------------------------------------------
// Referral Source Tags — LO-specific lead sources
// ---------------------------------------------------------------------------

const SOURCE_TAGS: { key: string; label: string; color: string; short: string }[] = [
  { key: 'ccr',                 label: 'Current Client Referral', color: '#27ae60', short: 'CCR' },
  { key: 'past_client_referral', label: 'Past Client Referral',  color: '#2980b9', short: 'PCR' },
  { key: 'past_client',         label: 'Past Client',            color: '#3498db', short: 'PC' },
  { key: 'personal_friend',     label: 'Personal Friend',        color: '#8e44ad', short: 'Friend' },
  { key: 'realtor',             label: 'Realtor',                color: '#e67e22', short: 'Realtor' },
  { key: 'ad',                  label: 'Advertisement',          color: '#c0392b', short: 'Ad' },
  { key: 'vip',                 label: 'VIP Client',             color: '#f1c40f', short: 'VIP' },
  { key: 'builder',             label: 'Builder',                color: '#16a085', short: 'Builder' },
];

interface StageConfig {
  key: string;
  label: string;
  color: string;
  icon: any;
  collapsed?: boolean;
}

// ---------------------------------------------------------------------------
// Stage Configuration
// ---------------------------------------------------------------------------

// KOPS-642: Added new_lead, new_contact, discovery stages for lender pipeline visibility
const STAGES: StageConfig[] = [
  { key: 'new_lead',         label: 'New Lead',                     color: '#e74c3c', icon: faFire },
  { key: 'new_contact',      label: 'New Contact',                  color: '#e57373', icon: faUser },
  { key: 'discovery',        label: 'Discovery',                    color: '#ff7043', icon: faSearch },
  { key: 'hot_lead',         label: 'Hot Lead',                     color: '#ef5350', icon: faFire },
  { key: 'active_buyer',     label: 'Loan Application',             color: '#e67e22', icon: faFileAlt },
  { key: 'pre_qualified',    label: 'Pre-Qualified',                color: '#f1c40f', icon: faCheckCircle },
  { key: 'under_contract',   label: 'Under Contract / Active Refi', color: '#27ae60', icon: faHandshake },
  { key: 'disclosures',      label: 'Disclosures',                  color: '#3498db', icon: faClipboardList },
  { key: 'initial_approval', label: 'Initial Approval / Conditions',color: '#2980b9', icon: faThumbsUp },
  { key: 'closing_docs',     label: 'Closing Docs',                 color: '#8e44ad', icon: faFolder },
  { key: 'past_buyer',       label: 'Funded / Completed',           color: '#2ecc71', icon: faTrophy },
];

const COLLAPSED_STAGES: StageConfig[] = [
  { key: 'cold_reactivation', label: 'Cold / Lost',       color: '#95a5a6', icon: faSnowflake, collapsed: true },
  { key: 'nurture',           label: 'Credit Repair',     color: '#e91e63', icon: faHeart, collapsed: true },
];

const ALL_STAGES = [...STAGES, ...COLLAPSED_STAGES];

// ---------------------------------------------------------------------------
// Loan program badge colors
// ---------------------------------------------------------------------------

const PROGRAM_COLORS: Record<string, string> = {
  VA:   '#2e7d32',
  FHA:  '#1565c0',
  Conv: '#6a1b9a',
  USDA: '#e65100',
  Conventional: '#6a1b9a',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const KanbanDealBoard: React.FC = () => {
  const [stages, setStages] = useState<Record<string, DealCard[]>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedCollapsed, setExpandedCollapsed] = useState<Record<string, boolean>>({});
  const [noteModal, setNoteModal] = useState<{ leadId: string; name: string } | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // KOPS-577: Referred deals from agents (via lead_deals linking)
  const [referredDeals, setReferredDeals] = useState<Record<string, { agentName: string; sharedAt: string }>>({});

  // Detail modal state
  const [detailCard, setDetailCard] = useState<{ card: DealCard; stageKey: string } | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailNoteText, setDetailNoteText] = useState('');
  const [detailNoteSaving, setDetailNoteSaving] = useState(false);

  // Fetch deal board data
  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/sponsor/deal-board');
      if (data.success) {
        setStages(data.stages || {});
        setCounts(data.counts || {});
      }
    } catch (err: any) {
      console.error('Deal board load failed:', err);
      toast.error('Failed to load deal board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // KOPS-577: Fetch agent-referred deals for badge display
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/agent/lender-referred-deals');
        if (data.success) setReferredDeals(data.referredDeals || {});
      } catch {}
    })();
  }, [loading]);

  // Handle drag end
  const onDragEnd = useCallback(async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const srcStage = source.droppableId;
    const dstStage = destination.droppableId;

    // Optimistic update
    setStages(prev => {
      const next = { ...prev };
      const srcCards = [...(next[srcStage] || [])];
      const [moved] = srcCards.splice(source.index, 1);
      next[srcStage] = srcCards;

      const dstCards = srcStage === dstStage ? srcCards : [...(next[dstStage] || [])];
      dstCards.splice(destination.index, 0, { ...moved, daysInStage: 0 });
      next[dstStage] = dstCards;
      return next;
    });

    // Update counts optimistically
    if (srcStage !== dstStage) {
      setCounts(prev => ({
        ...prev,
        [srcStage]: Math.max(0, (prev[srcStage] || 0) - 1),
        [dstStage]: (prev[dstStage] || 0) + 1,
      }));
    }

    // API call
    if (srcStage !== dstStage) {
      try {
        await axiosInstance.patch(`/sponsor/deal-board/${draggableId}/stage`, {
          stage: dstStage,
          previousStage: srcStage,
        });
        const stageLabel = ALL_STAGES.find(s => s.key === dstStage)?.label || dstStage;
        toast.success(`Moved to ${stageLabel}`);
      } catch {
        toast.error('Failed to move deal — refreshing');
        fetchBoard();
      }
    }
  }, [fetchBoard]);

  // Add note
  const handleAddNote = useCallback(async () => {
    if (!noteModal || !noteText.trim()) return;
    setNoteSaving(true);
    try {
      await axiosInstance.post(`/sponsor/deal-board/${noteModal.leadId}/note`, { note: noteText.trim() });
      toast.success('Note added');
      setNoteModal(null);
      setNoteText('');
      fetchBoard();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setNoteSaving(false);
    }
  }, [noteModal, noteText, fetchBoard]);

  // Open detail modal
  const openDetail = useCallback(async (card: DealCard, stageKey: string) => {
    setDetailCard({ card, stageKey });
    setDetailData(null);
    setDetailLoading(true);
    setDetailNoteText('');
    try {
      const { data } = await axiosInstance.get(`/sponsor/deal-board/${card.id}/detail`);
      if (data.success) setDetailData(data);
    } catch {
      toast.error('Failed to load detail');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Add note from detail modal
  const handleDetailNote = useCallback(async () => {
    if (!detailCard || !detailNoteText.trim()) return;
    setDetailNoteSaving(true);
    try {
      await axiosInstance.post(`/sponsor/deal-board/${detailCard.card.id}/note`, { note: detailNoteText.trim() });
      toast.success('Note added');
      setDetailNoteText('');
      // Refresh detail
      const { data } = await axiosInstance.get(`/sponsor/deal-board/${detailCard.card.id}/detail`);
      if (data.success) setDetailData(data);
      fetchBoard();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setDetailNoteSaving(false);
    }
  }, [detailCard, detailNoteText, fetchBoard]);

  // Change stage from detail modal
  const handleDetailStageChange = useCallback(async (newStage: string) => {
    if (!detailCard) return;
    const oldStage = detailCard.stageKey;
    try {
      await axiosInstance.patch(`/sponsor/deal-board/${detailCard.card.id}/stage`, {
        stage: newStage,
        previousStage: oldStage,
      });
      const stageLabel = ALL_STAGES.find(s => s.key === newStage)?.label || newStage;
      toast.success(`Moved to ${stageLabel}`);
      setDetailCard(prev => prev ? { ...prev, stageKey: newStage } : null);
      // Refresh detail + board
      const { data } = await axiosInstance.get(`/sponsor/deal-board/${detailCard.card.id}/detail`);
      if (data.success) setDetailData(data);
      fetchBoard();
    } catch {
      toast.error('Failed to change stage');
    }
  }, [detailCard, fetchBoard]);

  // Filter cards by search
  const filteredStages = useMemo(() => {
    if (!search) return stages;
    const q = search.toLowerCase();
    const filtered: Record<string, DealCard[]> = {};
    for (const [stage, cards] of Object.entries(stages)) {
      filtered[stage] = cards.filter(c =>
        c.borrowerName.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        c.source.toLowerCase().includes(q) ||
        c.loanProgram.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [stages, search]);

  const totalLeads = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: colors.offBg }}>
        <div style={{ textAlign: 'center' }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 32, color: colors.gold, marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: colors.muted }}>Loading deal board...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px', background: colors.navy, display: 'flex', alignItems: 'center', gap: 16,
        borderBottom: `2px solid ${colors.gold}`,
      }}>
        <h2 style={{ margin: 0, fontSize: 22, fontFamily: fonts.display, color: '#fff', letterSpacing: 1 }}>
          Deal Board
        </h2>
        <span style={{ fontSize: 12, color: colors.gold, fontWeight: 600 }}>
          {totalLeads} deals
        </span>

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ position: 'relative', width: 240 }}>
          <FontAwesomeIcon icon={faSearch} style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: colors.muted, fontSize: 12,
          }} />
          <input
            placeholder="Search borrowers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 32px', border: 'none', borderRadius: 6,
              background: colors.navy2, color: '#fff', fontSize: 12, outline: 'none',
              fontFamily: fonts.body,
            }}
          />
        </div>

        <button
          onClick={fetchBoard}
          style={{
            background: 'transparent', border: `1px solid ${colors.gold}`, borderRadius: 6,
            padding: '6px 12px', color: colors.gold, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <FontAwesomeIcon icon={faSyncAlt} style={{ marginRight: 4 }} /> Refresh
        </button>
      </div>

      {/* Stage count bar */}
      <div style={{
        display: 'flex', gap: 0, background: '#fff', borderBottom: '1px solid #e0e0e0',
        overflowX: 'auto',
      }}>
        {STAGES.map(stage => (
          <div key={stage.key} style={{
            flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #eee',
            minWidth: 100,
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: stage.color }}>
              {counts[stage.key] || 0}
            </div>
            <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
              {stage.label}
            </div>
          </div>
        ))}
      </div>

      {/* Kanban columns */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{
          display: 'flex', gap: 8, padding: '12px 12px', flex: 1, overflowX: 'auto',
          overflowY: 'hidden',
        }}>
          {/* Main stages */}
          {STAGES.map(stage => (
            <KanbanColumn
              key={stage.key}
              stage={stage}
              cards={filteredStages[stage.key] || []}
              count={counts[stage.key] || 0}
              onAddNote={(leadId, name) => { setNoteModal({ leadId, name }); setNoteText(''); }}
              onCardClick={(card) => openDetail(card, stage.key)}
              referredDeals={referredDeals}
            />
          ))}

          {/* Collapsed stages */}
          {COLLAPSED_STAGES.map(stage => {
            const expanded = expandedCollapsed[stage.key];
            const cards = filteredStages[stage.key] || [];
            const count = counts[stage.key] || 0;

            return (
              <div key={stage.key} style={{
                minWidth: expanded ? 260 : 44, maxWidth: expanded ? 280 : 44,
                background: '#e8eaed', borderRadius: 8, transition: 'all 0.2s',
                display: 'flex', flexDirection: 'column',
              }}>
                <button
                  onClick={() => setExpandedCollapsed(p => ({ ...p, [stage.key]: !p[stage.key] }))}
                  style={{
                    background: 'none', border: 'none', padding: expanded ? '12px' : '12px 0',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                    justifyContent: expanded ? 'flex-start' : 'center',
                    writingMode: expanded ? undefined : 'vertical-rl',
                    textOrientation: expanded ? undefined : 'mixed',
                  }}
                >
                  <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight}
                    style={{ fontSize: 10, color: stage.color }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>
                    {stage.label} ({count})
                  </span>
                </button>
                {expanded && (
                  <KanbanColumn stage={stage} cards={cards} count={count}
                    onAddNote={(leadId, name) => { setNoteModal({ leadId, name }); setNoteText(''); }}
                    onCardClick={(card) => openDetail(card, stage.key)}
                    isCollapsedExpanded
                    referredDeals={referredDeals} />
                )}
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Note Modal */}
      {noteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999,
        }}
          onClick={() => setNoteModal(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: 24, width: 420,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: colors.navy }}>
              Add Note
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: colors.muted }}>
              {noteModal.name}
            </p>
            <textarea
              autoFocus
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Type your note here..."
              rows={4}
              style={{
                width: '100%', border: `1px solid ${colors.border}`, borderRadius: 8,
                padding: 12, fontSize: 13, fontFamily: fonts.body, resize: 'vertical',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setNoteModal(null)}
                style={{
                  padding: '8px 16px', fontSize: 13, border: `1px solid ${colors.border}`,
                  borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#555',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={noteSaving || !noteText.trim()}
                style={{
                  padding: '8px 16px', fontSize: 13, border: 'none', borderRadius: 6,
                  background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                  color: colors.navy, fontWeight: 700, cursor: 'pointer',
                  opacity: noteSaving || !noteText.trim() ? 0.5 : 1,
                }}
              >
                {noteSaving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Deal Detail Modal ── */}
      {detailCard && (
        <DealDetailModal
          card={detailCard.card}
          stageKey={detailCard.stageKey}
          detail={detailData}
          loading={detailLoading}
          noteText={detailNoteText}
          noteSaving={detailNoteSaving}
          onNoteChange={setDetailNoteText}
          onAddNote={handleDetailNote}
          onStageChange={handleDetailStageChange}
          onRefreshDetail={async () => {
            const { data } = await axiosInstance.get(`/sponsor/deal-board/${detailCard.card.id}/detail`);
            if (data.success) setDetailData(data);
          }}
          onRefreshBoard={fetchBoard}
          onClose={() => { setDetailCard(null); setDetailData(null); }}
          referredBy={referredDeals[detailCard.card.id]}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Deal Detail Modal
// ---------------------------------------------------------------------------

const DealDetailModal: React.FC<{
  card: DealCard;
  stageKey: string;
  detail: any;
  loading: boolean;
  noteText: string;
  noteSaving: boolean;
  onNoteChange: (v: string) => void;
  onAddNote: () => void;
  onStageChange: (stage: string) => void;
  onRefreshDetail: () => void;
  onRefreshBoard: () => void;
  onClose: () => void;
  referredBy?: { agentName: string; sharedAt: string };
}> = ({ card, stageKey, detail, loading, noteText, noteSaving, onNoteChange, onAddNote, onStageChange, onRefreshDetail, onRefreshBoard, onClose, referredBy }) => {
  const currentStage = ALL_STAGES.find(s => s.key === (detail?.detail?.stage || stageKey));
  const d = detail?.detail;
  const activities = detail?.activities || [];

  // KOPS-576: Notify Borrower state
  const [notifyDraft, setNotifyDraft] = useState<any>(null);
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyBody, setNotifyBody] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyScheduleDate, setNotifyScheduleDate] = useState('');

  // KOPS-642: Send Note to Agent Partner state
  const [agentNoteText, setAgentNoteText] = useState('');
  const [agentNoteSending, setAgentNoteSending] = useState(false);
  const [agentNoteSent, setAgentNoteSent] = useState(false);

  const assignedAgentName = d?.tags?.assigned_agent_name || d?.assignedAgent?.name || '';
  const assignedAgentEmail = d?.tags?.assigned_agent_email || d?.assignedAgent?.email || '';

  const handleSendNoteToAgent = async () => {
    if (!agentNoteText.trim() || !card.id) return;
    setAgentNoteSending(true);
    try {
      await axiosInstance.post(`/sponsor/deal-board/${card.id}/notify-agent`, {
        message: agentNoteText.trim(),
      });
      toast.success(`Sent to ${assignedAgentName || assignedAgentEmail}`);
      setAgentNoteSent(true);
      setAgentNoteText('');
      onRefreshDetail();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to send note');
    }
    setAgentNoteSending(false);
  };

  // Source tag + realtor state
  const [selectedSource, setSelectedSource] = useState<string>(d?.sourceTag || card.sourceTag || '');
  const [realtorName, setRealtorName] = useState<string>(d?.referringAgent || card.referringAgent || '');
  const [agentResults, setAgentResults] = useState<any[]>([]);
  const [agentSearching, setAgentSearching] = useState(false);
  const [sourceSaving, setSourceSaving] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);

  // Sync source state when detail loads
  useEffect(() => {
    if (d?.sourceTag) setSelectedSource(d.sourceTag);
    if (d?.referringAgent) setRealtorName(d.referringAgent);
  }, [d?.sourceTag, d?.referringAgent]);

  // Search sponsored agents for realtor lookup
  const searchAgents = useCallback(async (query: string) => {
    setRealtorName(query);
    if (query.length < 2) { setAgentResults([]); setShowAgentDropdown(false); return; }
    setAgentSearching(true);
    try {
      const { data } = await axiosInstance.get('/lender/agents');
      if (data.success) {
        const q = query.toLowerCase();
        const matches = (data.agents || []).filter((a: any) =>
          a.name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
        ).slice(0, 8);
        setAgentResults(matches);
        setShowAgentDropdown(matches.length > 0);
      }
    } catch {}
    setAgentSearching(false);
  }, []);

  // Save source tag + realtor
  const saveSource = useCallback(async (sourceKey: string, agent?: string) => {
    setSourceSaving(true);
    try {
      await axiosInstance.patch(`/sponsor/deal-board/${card.id}/source`, {
        sourceTag: sourceKey,
        referringAgent: agent ?? realtorName,
      });
      toast.success('Source updated');
      onRefreshDetail();
      onRefreshBoard();
    } catch {
      toast.error('Failed to update source');
    } finally {
      setSourceSaving(false);
    }
  }, [card.id, realtorName, onRefreshDetail, onRefreshBoard]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, width: 680, maxHeight: '90vh',
        boxShadow: '0 24px 80px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          background: colors.navy, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16,
          borderBottom: `3px solid ${currentStage?.color || colors.gold}`,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: currentStage?.color || colors.gold,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 18,
          }}>
            {card.borrowerName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontFamily: fonts.display, letterSpacing: 1 }}>
              <a
                href={`/agent-portal/clients?id=${card.id}&tab=overview`}
                style={{ color: '#fff', textDecoration: 'none', borderBottom: '1px dashed rgba(255,255,255,0.4)' }}
                onMouseEnter={e => (e.currentTarget.style.color = colors.gold2)}
                onMouseLeave={e => (e.currentTarget.style.color = '#fff')}
                title="Open full client profile"
              >
                {card.borrowerName}
              </a>
            </h2>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10,
                background: `${currentStage?.color}33`, color: currentStage?.color || colors.gold,
              }}>
                <FontAwesomeIcon icon={currentStage?.icon || faUser} style={{ marginRight: 4 }} />
                {currentStage?.label || stageKey}
              </span>
              <span style={{ fontSize: 10, color: colors.muted }}>
                <FontAwesomeIcon icon={faClock} style={{ marginRight: 3 }} />
                {card.daysInStage}d in stage
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: colors.muted, fontSize: 20,
            cursor: 'pointer', padding: 4,
          }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* KOPS-577: Referred by Agent banner */}
        {referredBy && (
          <div style={{
            background: '#e8f5e9', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: '1px solid #c8e6c9',
          }}>
            <FontAwesomeIcon icon={faUser} style={{ color: '#27ae60', fontSize: 12 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#2e7d32' }}>
              Referred by: {referredBy.agentName}
            </span>
            {referredBy.sharedAt && (
              <span style={{ fontSize: 11, color: '#66bb6a', marginLeft: 'auto' }}>
                {new Date(referredBy.sharedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 28, color: colors.gold }} />
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: 0 }}>
            {/* Contact + Loan Info Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              {/* Left: Contact */}
              <div style={{ padding: '20px 24px', borderRight: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                  Contact
                </div>
                {(d?.phone || card.phone) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <FontAwesomeIcon icon={faPhone} style={{ color: '#27ae60', fontSize: 12, width: 14 }} />
                    <a href={`tel:${d?.phone || card.phone}`} style={{ color: colors.navy, fontSize: 13, textDecoration: 'none' }}>
                      {d?.phone || card.phone}
                    </a>
                  </div>
                )}
                {(d?.email || card.email) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ color: '#3498db', fontSize: 12, width: 14 }} />
                    <a href={`mailto:${d?.email || card.email}`} style={{ color: colors.navy, fontSize: 13, textDecoration: 'none' }}>
                      {d?.email || card.email}
                    </a>
                  </div>
                )}
                {d?.address && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#e67e22', fontSize: 12, width: 14 }} />
                    <span style={{ fontSize: 13, color: '#555' }}>{d.address}</span>
                  </div>
                )}
              </div>

              {/* Right: Loan Details */}
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                  Loan Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {(d?.loanProgram || card.loanProgram) && (
                    <div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>Program</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{d?.loanProgram || card.loanProgram}</div>
                    </div>
                  )}
                  {(d?.loanType || card.loanType) && (
                    <div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>Type</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{d?.loanType || card.loanType}</div>
                    </div>
                  )}
                  {(d?.coeDate || card.coeDate) && (
                    <div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>COE Date</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#27ae60' }}>
                        <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: 4 }} />
                        {d?.coeDate || card.coeDate}
                      </div>
                    </div>
                  )}
                  {d?.preApprovalAmount && (
                    <div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>Pre-Approval</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>
                        <FontAwesomeIcon icon={faDollarSign} style={{ marginRight: 2 }} />
                        {Number(d.preApprovalAmount).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {d?.purchasePrice && (
                    <div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>Purchase Price</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>
                        <FontAwesomeIcon icon={faHome} style={{ marginRight: 2 }} />
                        ${Number(d.purchasePrice).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {d?.propertyType && (
                    <div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>Property Type</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{d.propertyType}</div>
                    </div>
                  )}
                  {d?.confidence != null && (
                    <div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>Confidence</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: d.confidence > 0.7 ? '#27ae60' : d.confidence > 0.4 ? '#e67e22' : '#e74c3c' }}>
                        {Math.round(d.confidence * 100)}%
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Referral Source */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                Lead Source
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: selectedSource === 'realtor' ? 12 : 0 }}>
                {SOURCE_TAGS.map(tag => {
                  const isActive = selectedSource === tag.key;
                  return (
                    <button
                      key={tag.key}
                      onClick={() => {
                        setSelectedSource(tag.key);
                        if (tag.key !== 'realtor') {
                          setRealtorName('');
                          saveSource(tag.key, '');
                        }
                      }}
                      disabled={sourceSaving}
                      style={{
                        padding: '5px 10px', fontSize: 10, fontWeight: 600, borderRadius: 6,
                        border: isActive ? `2px solid ${tag.color}` : '1px solid #ddd',
                        background: isActive ? `${tag.color}18` : '#fff',
                        color: isActive ? tag.color : '#666',
                        cursor: 'pointer',
                      }}
                    >
                      {tag.label}
                    </button>
                  );
                })}
              </div>

              {/* Realtor lookup — only shown when source is Realtor */}
              {selectedSource === 'realtor' && (
                <div style={{ position: 'relative' }}>
                  <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>
                    Referring Agent — search your agent community or type a name
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        placeholder="Search agent name..."
                        value={realtorName}
                        onChange={e => searchAgents(e.target.value)}
                        onFocus={() => { if (agentResults.length > 0) setShowAgentDropdown(true); }}
                        onBlur={() => setTimeout(() => setShowAgentDropdown(false), 200)}
                        style={{
                          width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
                          borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: fonts.body,
                        }}
                      />
                      {agentSearching && (
                        <FontAwesomeIcon icon={faSpinner} spin style={{
                          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                          color: colors.muted, fontSize: 12,
                        }} />
                      )}
                      {/* Agent dropdown */}
                      {showAgentDropdown && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                          background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto',
                          marginTop: 2,
                        }}>
                          {agentResults.map((agent: any) => (
                            <div
                              key={agent.id}
                              onMouseDown={() => {
                                setRealtorName(agent.name);
                                setShowAgentDropdown(false);
                                saveSource('realtor', agent.name);
                              }}
                              style={{
                                padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                                borderBottom: '1px solid #f5f5f5',
                              }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f8f8f8')}
                              onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                            >
                              <div style={{ fontWeight: 600, color: colors.navy }}>{agent.name}</div>
                              <div style={{ fontSize: 10, color: '#888' }}>{agent.email}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => saveSource('realtor')}
                      disabled={sourceSaving || !realtorName.trim()}
                      style={{
                        padding: '8px 14px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                        color: colors.navy, cursor: 'pointer',
                        opacity: sourceSaving || !realtorName.trim() ? 0.5 : 1,
                      }}
                    >
                      {sourceSaving ? '...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Stage Selector */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                Move to Stage
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {ALL_STAGES.map(s => {
                  const isActive = s.key === (d?.stage || stageKey);
                  return (
                    <button
                      key={s.key}
                      onClick={() => { if (!isActive) onStageChange(s.key); }}
                      style={{
                        padding: '5px 10px', fontSize: 10, fontWeight: 600, borderRadius: 6,
                        border: isActive ? `2px solid ${s.color}` : '1px solid #ddd',
                        background: isActive ? `${s.color}18` : '#fff',
                        color: isActive ? s.color : '#666',
                        cursor: isActive ? 'default' : 'pointer',
                      }}
                    >
                      <FontAwesomeIcon icon={s.icon} style={{ marginRight: 4, fontSize: 9 }} />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* KOPS-576: Notify Borrower */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee' }}>
              <button
                onClick={async () => {
                  if (notifyDraft) { setNotifyDraft(null); return; }
                  setNotifyLoading(true);
                  try {
                    const stageName = ALL_STAGES.find(s => s.key === (d?.stage || stageKey))?.label || stageKey;
                    const { data } = await axiosInstance.post(`/deal/${card.id}/notify-borrower`, { stage_name: stageName });
                    if (data?.draft_subject) {
                      setNotifyDraft(data);
                      setNotifySubject(data.draft_subject);
                      setNotifyBody(data.draft_body);
                    } else {
                      toast.error('Could not generate notification draft');
                    }
                  } catch { toast.error('Failed to generate notification'); }
                  setNotifyLoading(false);
                }}
                disabled={notifyLoading}
                style={{
                  width: '100%', padding: '10px 16px', fontSize: 12, fontWeight: 700, borderRadius: 8,
                  border: notifyDraft ? `2px solid ${colors.gold}` : `1px solid ${colors.gold}`,
                  background: notifyDraft ? `${colors.gold}15` : `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                  color: notifyDraft ? colors.navy : colors.navy,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                {notifyLoading ? 'Drafting...' : notifyDraft ? 'Hide Draft' : 'Notify Borrower'}
              </button>

              {/* Editable Draft Email */}
              {notifyDraft && (
                <div style={{ marginTop: 12, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Stage Update Email — Edit & Send
                    </div>
                    {notifyDraft.compliance && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: notifyDraft.compliance.aiAuditPassed
                          ? (notifyDraft.compliance.autoCleaned ? '#fff3cd' : '#d4edda')
                          : '#f8d7da',
                        color: notifyDraft.compliance.aiAuditPassed
                          ? (notifyDraft.compliance.autoCleaned ? '#856404' : '#155724')
                          : '#721c24',
                      }}>
                        {notifyDraft.compliance.aiAuditPassed
                          ? (notifyDraft.compliance.autoCleaned ? `Compliance: Auto-Cleaned` : `Compliance: Passed`)
                          : `Compliance: Review Required`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
                    To: <strong>{notifyDraft.borrower_email || card.borrowerName}</strong>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: '#666', display: 'block', marginBottom: 3 }}>Subject</label>
                    <input
                      type="text"
                      value={notifySubject}
                      onChange={e => setNotifySubject(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', fontSize: 12, fontWeight: 600, borderRadius: 4, border: '1px solid #ddd', outline: 'none' }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: '#666', display: 'block', marginBottom: 3 }}>Body</label>
                    <textarea
                      value={notifyBody}
                      onChange={e => setNotifyBody(e.target.value)}
                      rows={8}
                      style={{ width: '100%', padding: 10, fontSize: 12, lineHeight: 1.6, borderRadius: 4, border: '1px solid #ddd', outline: 'none', resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={async () => {
                        setNotifySending(true);
                        try {
                          await axiosInstance.post(`/ai-coach/send-email`, {
                            leadId: notifyDraft.lead_id || card.id,
                            subject: notifySubject,
                            body: notifyBody,
                            toEmail: notifyDraft.borrower_email,
                          });
                          // Record feedback for tone learning
                          await axiosInstance.post(`/deal/${card.id}/notify-borrower/feedback`, {
                            stage_name: notifyDraft.stage,
                            ai_draft: { subject: notifyDraft.draft_subject, body: notifyDraft.draft_body },
                            final_version: { subject: notifySubject, body: notifyBody },
                            was_edited: notifySubject !== notifyDraft.draft_subject || notifyBody !== notifyDraft.draft_body,
                          }).catch(() => {});
                          toast.success(`Email sent to ${notifyDraft.borrower_name || 'borrower'}`);
                          setNotifyDraft(null);
                          setNotifySubject('');
                          setNotifyBody('');
                          onRefreshDetail();
                        } catch { toast.error('Failed to send — check connected email'); }
                        setNotifySending(false);
                      }}
                      disabled={notifySending}
                      style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#27ae60', color: 'white', cursor: 'pointer', opacity: notifySending ? 0.6 : 1 }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 4 }} />
                      {notifySending ? 'Sending...' : 'Send to Borrower'}
                    </button>
                    <button
                      onClick={() => { setNotifyDraft(null); setNotifySubject(''); setNotifyBody(''); setNotifyScheduleDate(''); }}
                      style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid #ddd', background: 'white', color: '#666', cursor: 'pointer' }}
                    >
                      Dismiss
                    </button>
                  </div>

                  {/* Schedule Send */}
                  <div style={{ marginTop: 10, padding: '10px 0', borderTop: '1px solid #eee' }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: '#888', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <FontAwesomeIcon icon={faClock} style={{ fontSize: 10 }} /> Schedule Send (Optional)
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="datetime-local"
                        value={notifyScheduleDate}
                        onChange={e => setNotifyScheduleDate(e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, color: colors.navy }}
                      />
                      <span style={{ fontSize: 10, color: '#aaa' }}>Leave blank to send immediately</span>
                      {notifyScheduleDate && (
                        <button
                          onClick={async () => {
                            setNotifySending(true);
                            try {
                              await axiosInstance.post('/ai-coach/send-email', {
                                leadId: notifyDraft.lead_id || card.id,
                                subject: notifySubject,
                                body: notifyBody,
                                toEmail: notifyDraft.borrower_email,
                                scheduledFor: new Date(notifyScheduleDate).toISOString(),
                              });
                              await axiosInstance.post(`/deal/${card.id}/notify-borrower/feedback`, {
                                stage_name: notifyDraft.stage,
                                ai_draft: { subject: notifyDraft.draft_subject, body: notifyDraft.draft_body },
                                final_version: { subject: notifySubject, body: notifyBody },
                                was_edited: notifySubject !== notifyDraft.draft_subject || notifyBody !== notifyDraft.draft_body,
                              }).catch(() => {});
                              toast.success(`Scheduled for ${new Date(notifyScheduleDate).toLocaleString()}`);
                              setNotifyDraft(null); setNotifySubject(''); setNotifyBody(''); setNotifyScheduleDate('');
                              onRefreshDetail();
                            } catch { toast.error('Failed to schedule'); }
                            setNotifySending(false);
                          }}
                          disabled={notifySending}
                          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: `1px solid ${colors.navy}`, background: 'white', color: colors.navy, cursor: 'pointer' }}
                        >
                          <FontAwesomeIcon icon={faClock} style={{ marginRight: 4 }} />
                          {notifySending ? 'Scheduling...' : `Schedule for ${new Date(notifyScheduleDate).toLocaleDateString()}`}
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 10, fontWeight: 600, color: '#888', display: 'block', marginBottom: 3 }}>
                        Want something different? Tell AI what to change:
                      </label>
                      <input
                        type="text"
                        placeholder='e.g. "make it shorter" or "mention the VA loan" or "more friendly tone"'
                        value={(notifyDraft as any)?._regenInstruction || ''}
                        onChange={e => setNotifyDraft((prev: any) => ({ ...prev, _regenInstruction: e.target.value }))}
                        onKeyDown={async (e) => {
                          if (e.key !== 'Enter' || notifyLoading) return;
                          const instruction = (notifyDraft as any)?._regenInstruction;
                          if (!instruction) return;
                          setNotifyLoading(true);
                          try {
                            const stageName = ALL_STAGES.find(s => s.key === (d?.stage || stageKey))?.label || stageKey;
                            const { data } = await axiosInstance.post(`/deal/${card.id}/notify-borrower`, { stage_name: stageName, instruction });
                            if (data?.draft_subject) { setNotifySubject(data.draft_subject); setNotifyBody(data.draft_body); toast.success('Draft regenerated'); }
                          } catch { toast.error('Failed to regenerate'); }
                          setNotifyLoading(false);
                        }}
                        style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 4, border: '1px solid #ddd', outline: 'none' }}
                      />
                    </div>
                    <button
                      onClick={async () => {
                        setNotifyLoading(true);
                        try {
                          const stageName = ALL_STAGES.find(s => s.key === (d?.stage || stageKey))?.label || stageKey;
                          const instruction = (notifyDraft as any)?._regenInstruction || '';
                          const { data } = await axiosInstance.post(`/deal/${card.id}/notify-borrower`, { stage_name: stageName, instruction: instruction || undefined });
                          if (data?.draft_subject) { setNotifySubject(data.draft_subject); setNotifyBody(data.draft_body); toast.success('Draft regenerated'); }
                        } catch { toast.error('Failed to regenerate'); }
                        setNotifyLoading(false);
                      }}
                      disabled={notifyLoading}
                      style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: colors.navy, color: 'white', cursor: 'pointer', whiteSpace: 'nowrap', opacity: notifyLoading ? 0.6 : 1 }}
                    >
                      {notifyLoading ? 'Regenerating...' : 'Regenerate'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Note */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                Add Note
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  placeholder="Type a note..."
                  value={noteText}
                  onChange={e => onNoteChange(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && noteText.trim()) onAddNote(); }}
                  style={{
                    flex: 1, padding: '8px 12px', border: `1px solid ${colors.border}`,
                    borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: fonts.body,
                  }}
                />
                <button
                  onClick={onAddNote}
                  disabled={noteSaving || !noteText.trim()}
                  style={{
                    padding: '8px 16px', border: 'none', borderRadius: 6,
                    background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                    color: colors.navy, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                    opacity: noteSaving || !noteText.trim() ? 0.5 : 1,
                  }}
                >
                  <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 4 }} />
                  {noteSaving ? '...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Send Note to Agent Partner */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 4 }} />
                Send Status Update to Agent
              </div>
              {assignedAgentEmail ? (
                <>
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                    To: <strong>{assignedAgentName}</strong> ({assignedAgentEmail})
                  </div>
                  {/* Quick message buttons */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    {['Pre-approved — ready for next steps', 'Under contract — processing docs', 'Closing scheduled — almost done', 'Funded — deal complete!'].map((msg) => (
                      <button key={msg} onClick={() => { setAgentNoteText(msg); setAgentNoteSent(false); }}
                        style={{ padding: '4px 10px', border: `1px solid ${colors.border || '#ddd'}`, borderRadius: 20, fontSize: 11, color: '#555', background: agentNoteText === msg ? `${colors.gold}15` : '#f8f9fa', cursor: 'pointer', fontFamily: fonts.body }}>
                        {msg.split('—')[0].trim()}
                      </button>
                    ))}
                  </div>
                  <textarea
                    placeholder="Type a status update for the agent partner..."
                    value={agentNoteText}
                    onChange={e => { setAgentNoteText(e.target.value); setAgentNoteSent(false); }}
                    rows={3}
                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border || '#ddd'}`, borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: fonts.body, resize: 'vertical', marginBottom: 8 }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      onClick={handleSendNoteToAgent}
                      disabled={agentNoteSending || !agentNoteText.trim()}
                      style={{
                        padding: '8px 20px', border: 'none', borderRadius: 6,
                        background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                        color: colors.navy, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                        opacity: agentNoteSending || !agentNoteText.trim() ? 0.5 : 1,
                      }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 4 }} />
                      {agentNoteSending ? 'Sending...' : 'Send to Agent'}
                    </button>
                    {agentNoteSent && (
                      <span style={{ fontSize: 12, color: '#27ae60', fontWeight: 600 }}>✓ Sent to {assignedAgentName}</span>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', padding: 8 }}>
                  No agent assigned to this deal. Use the Lead Source section above to assign a realtor first.
                </div>
              )}
            </div>

            {/* Activity Timeline */}
            <div style={{ padding: '16px 24px 24px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                <FontAwesomeIcon icon={faHistory} style={{ marginRight: 4 }} />
                Activity History ({activities.length})
              </div>
              {activities.length === 0 && (
                <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', padding: 8 }}>No activity recorded yet.</div>
              )}
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {activities.map((a: any, i: number) => (
                  <div key={a.id || i} style={{
                    display: 'flex', gap: 12, padding: '8px 0',
                    borderBottom: i < activities.length - 1 ? '1px solid #f5f5f5' : 'none',
                  }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      background: a.activity_type === 'stage_change' ? '#e3f2fd'
                        : a.activity_type === 'note' ? '#fff3e0'
                        : a.activity_type === 'call' ? '#e8f5e9'
                        : a.activity_type === 'email' ? '#ede7f6'
                        : '#f5f5f5',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FontAwesomeIcon
                        icon={
                          a.activity_type === 'stage_change' ? faArrowRight
                          : a.activity_type === 'note' ? faStickyNote
                          : a.activity_type === 'call' ? faPhone
                          : a.activity_type === 'email' ? faEnvelope
                          : faClipboardList
                        }
                        style={{
                          fontSize: 10,
                          color: a.activity_type === 'stage_change' ? '#1565c0'
                            : a.activity_type === 'note' ? '#e65100'
                            : a.activity_type === 'call' ? '#2e7d32'
                            : a.activity_type === 'email' ? '#6a1b9a'
                            : '#888',
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>
                        {a.subject || a.activity_type?.replace(/_/g, ' ')}
                      </div>
                      {a.description && (
                        <div style={{ fontSize: 11, color: '#666', marginTop: 2, lineHeight: 1.4 }}>
                          {a.description.substring(0, 200)}{a.description.length > 200 ? '...' : ''}
                        </div>
                      )}
                      {a.outcome && (
                        <div style={{ fontSize: 10, color: '#27ae60', marginTop: 2 }}>
                          Outcome: {a.outcome}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {a.activity_date ? new Date(a.activity_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div style={{
          padding: '12px 24px', borderTop: '1px solid #eee', background: '#fafafa',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          {(d?.phone || card.phone) && (
            <a href={`tel:${d?.phone || card.phone}`} style={{
              padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6,
              background: '#e8f5e9', color: '#2e7d32', textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <FontAwesomeIcon icon={faPhone} /> Call
            </a>
          )}
          {(d?.email || card.email) && (
            <a href={`mailto:${d?.email || card.email}`} style={{
              padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6,
              background: '#e3f2fd', color: '#1565c0', textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <FontAwesomeIcon icon={faEnvelope} /> Email
            </a>
          )}
          <button onClick={onClose} style={{
            padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6,
            border: `1px solid ${colors.border}`, background: '#fff', color: '#555',
            cursor: 'pointer',
          }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Column Component
// ---------------------------------------------------------------------------

const KanbanColumn: React.FC<{
  stage: StageConfig;
  cards: DealCard[];
  count: number;
  onAddNote: (leadId: string, name: string) => void;
  onCardClick: (card: DealCard) => void;
  isCollapsedExpanded?: boolean;
  referredDeals?: Record<string, { agentName: string; sharedAt: string }>;
}> = ({ stage, cards, count, onAddNote, onCardClick, isCollapsedExpanded, referredDeals }) => {
  return (
    <div style={{
      minWidth: isCollapsedExpanded ? undefined : 260,
      maxWidth: 280, flex: isCollapsedExpanded ? 1 : '0 0 260px',
      display: 'flex', flexDirection: 'column',
      background: '#ebecf0', borderRadius: 8,
    }}>
      {/* Column header */}
      {!isCollapsedExpanded && (
        <div style={{
          padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
          borderBottom: `3px solid ${stage.color}`,
        }}>
          <FontAwesomeIcon icon={stage.icon} style={{ color: stage.color, fontSize: 12 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#333', flex: 1 }}>
            {stage.label}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 700, color: '#fff', background: stage.color,
            borderRadius: 10, padding: '2px 8px', minWidth: 20, textAlign: 'center',
          }}>
            {count}
          </span>
        </div>
      )}

      {/* Cards */}
      <Droppable droppableId={stage.key}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              flex: 1, overflowY: 'auto', padding: 6, minHeight: 60,
              background: snapshot.isDraggingOver ? 'rgba(201,149,42,0.1)' : 'transparent',
              transition: 'background 0.2s',
            }}
          >
            {cards.map((card, index) => (
              <Draggable key={card.id} draggableId={card.id} index={index}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                      ...provided.draggableProps.style,
                      marginBottom: 6,
                      borderRadius: 6,
                      background: snapshot.isDragging ? '#fffde7' : '#fff',
                      boxShadow: snapshot.isDragging
                        ? '0 8px 24px rgba(0,0,0,0.15)'
                        : '0 1px 2px rgba(0,0,0,0.1)',
                      border: snapshot.isDragging ? `2px solid ${colors.gold}` : '1px solid #e0e0e0',
                      transition: 'box-shadow 0.2s',
                    }}
                  >
                    <DealCardView card={card} stageColor={stage.color} onAddNote={onAddNote} onCardClick={() => onCardClick(card)} referredBy={referredDeals?.[card.id]} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {cards.length === 0 && (
              <div style={{
                padding: 16, textAlign: 'center', fontSize: 11, color: '#aaa',
                fontStyle: 'italic',
              }}>
                No deals
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Card Component
// ---------------------------------------------------------------------------

const DealCardView: React.FC<{
  card: DealCard;
  stageColor: string;
  onAddNote: (leadId: string, name: string) => void;
  onCardClick: () => void;
  referredBy?: { agentName: string; sharedAt: string };
}> = ({ card, stageColor, onAddNote, onCardClick, referredBy }) => {
  const programColor = PROGRAM_COLORS[card.loanProgram] || PROGRAM_COLORS[card.loanProgram?.replace(/conventional/i, 'Conv')] || '#555';
  const shortProgram = card.loanProgram?.replace(/Conventional/i, 'Conv') || '';

  return (
    <div style={{ padding: '8px 10px', fontSize: 12, cursor: 'pointer' }} onClick={onCardClick}>
      {/* Borrower name */}
      <div style={{
        fontWeight: 700, color: '#222', fontSize: 13, marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <FontAwesomeIcon icon={faGripVertical} style={{ color: '#ccc', fontSize: 9 }} />
        {card.borrowerName}
      </div>

      {/* KOPS-577: Referred by Agent badge */}
      {referredBy && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4,
          background: '#e8f5e9', borderRadius: 4, padding: '2px 6px',
        }}>
          <FontAwesomeIcon icon={faUser} style={{ color: '#27ae60', fontSize: 9 }} />
          <span style={{ fontSize: 9, fontWeight: 700, color: '#2e7d32' }}>
            Referred by: {referredBy.agentName}
          </span>
        </div>
      )}

      {/* Badges row */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
        {shortProgram && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
            background: `${programColor}18`, color: programColor,
          }}>
            {shortProgram}
          </span>
        )}
        {card.loanType && (
          <span style={{
            fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
            background: '#f0f0f0', color: '#555',
          }}>
            {card.loanType}
          </span>
        )}
        {card.coeDate && (
          <span style={{
            fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
            background: '#e8f5e9', color: '#2e7d32',
          }}>
            <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: 2 }} />
            COE: {card.coeDate}
          </span>
        )}
      </div>

      {/* Source tag badge */}
      {(() => {
        const tag = SOURCE_TAGS.find(t => t.key === card.sourceTag);
        return (tag || card.source) ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
            {tag && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                background: `${tag.color}18`, color: tag.color,
              }}>
                {tag.short}
              </span>
            )}
            {card.sourceTag === 'realtor' && card.referringAgent && (
              <span style={{ fontSize: 9, color: '#e67e22', fontWeight: 600 }}>
                {card.referringAgent}
              </span>
            )}
            {!tag && card.source && (
              <span style={{ fontSize: 9, color: '#999' }}>
                {card.source}
              </span>
            )}
          </div>
        ) : null;
      })()}

      {/* Phone */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 4, color: '#888', fontSize: 10 }}>
        {card.phone && (
          <span>
            <FontAwesomeIcon icon={faPhone} style={{ marginRight: 3 }} />
            {card.phone}
          </span>
        )}
      </div>

      {/* Last note */}
      {card.lastNote?.text && (
        <div style={{
          fontSize: 10, color: '#666', background: '#f8f8f8', borderRadius: 4,
          padding: '4px 6px', marginBottom: 4, lineHeight: 1.4,
          maxHeight: 36, overflow: 'hidden',
        }}>
          <FontAwesomeIcon icon={faStickyNote} style={{ color: '#ddd', marginRight: 4 }} />
          {card.lastNote.text.substring(0, 80)}{card.lastNote.text.length > 80 ? '...' : ''}
        </div>
      )}

      {/* Footer: days in stage + actions */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: 4, paddingTop: 4, borderTop: '1px solid #f0f0f0',
      }}>
        <span style={{
          fontSize: 9, color: card.daysInStage > 14 ? '#e74c3c' : card.daysInStage > 7 ? '#e67e22' : '#888',
          fontWeight: card.daysInStage > 7 ? 700 : 400,
        }}>
          <FontAwesomeIcon icon={faClock} style={{ marginRight: 3 }} />
          {card.daysInStage}d in stage
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {card.phone && (
            <a href={`tel:${card.phone}`} style={{ color: '#27ae60', fontSize: 12 }} title="Call">
              <FontAwesomeIcon icon={faPhone} />
            </a>
          )}
          {card.email && (
            <a href={`mailto:${card.email}`} style={{ color: '#3498db', fontSize: 12 }} title="Email">
              <FontAwesomeIcon icon={faEnvelope} />
            </a>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onAddNote(card.id, card.borrowerName); }}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: '#999', fontSize: 12,
            }}
            title="Add note"
          >
            <FontAwesomeIcon icon={faStickyNote} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default KanbanDealBoard;
